
import React, { useRef, useState } from 'react';
import { Bot, X, Minimize, Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useIsMobile } from '@/hooks/use-mobile';
import { Sheet as MobileSidebar, SheetContent as MobileSidebarContent, SheetTrigger } from '@/components/ui/sheet';
import MessageList from './MessageList';
import ChatInput from './ChatInput';
import SessionSidebar from './SessionSidebar';
import { Message } from './types';
import { ChatSettingsConfig } from './ChatSettings';
import { useChatSessions } from '@/hooks/useChatSessions';

interface FullscreenChatDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  messages: Message[];
  isLoading: boolean;
  onSendMessage: (message: string, settings: ChatSettingsConfig) => void;
  onNewSession: () => void;
  onSessionSelect: (sessionId: string) => void;
  organisation?: {
    id: string;
    name: string;
    connections?: any[];
  };
  currentSessionId?: string;
  isLoadingHistory?: boolean;
  currentToolCall?: string | null;
  settings: ChatSettingsConfig;
  onSettingsChange: (settings: ChatSettingsConfig) => void;
}

const FullscreenChatDrawer: React.FC<FullscreenChatDrawerProps> = ({
  isOpen,
  onClose,
  messages,
  isLoading,
  onSendMessage,
  onNewSession,
  onSessionSelect,
  organisation,
  currentSessionId,
  isLoadingHistory = false,
  currentToolCall = null,
  settings,
  onSettingsChange
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const { sessions } = useChatSessions(organisation?.id);
  const isMobile = useIsMobile();

  const currentSession = sessions.find(s => s.session_id === currentSessionId);
  const currentSessionName = currentSession?.session_name || 'Chat Session';

  const handleSessionSelect = (sessionId: string) => {
    onSessionSelect(sessionId);
    setIsMobileSidebarOpen(false);
  };

  const handleNewSessionInDrawer = () => {
    onNewSession();
    setIsMobileSidebarOpen(false);
  };

  return (
    <Sheet data-testid="fullscreen-chat-drawer" open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full max-w-none sm:max-w-none p-0 flex">
        {/* Desktop Sidebar */}
        {!isMobile && (
          <SessionSidebar
            organisationId={organisation?.id}
            currentSessionId={currentSessionId}
            onSessionSelect={handleSessionSelect}
            onNewSession={handleNewSessionInDrawer}
            isCollapsed={isSidebarCollapsed}
            onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          />
        )}

        {/* Mobile Sidebar */}
        {isMobile && (
          <MobileSidebar open={isMobileSidebarOpen} onOpenChange={setIsMobileSidebarOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="absolute left-2 top-2 z-10 h-8 w-8 p-0"
              >
                <Menu className="h-4 w-4" />
              </Button>
            </SheetTrigger>
            <MobileSidebarContent side="left" className="p-0 w-80">
              <SessionSidebar
                organisationId={organisation?.id}
                currentSessionId={currentSessionId}
                onSessionSelect={handleSessionSelect}
                onNewSession={handleNewSessionInDrawer}
              />
            </MobileSidebarContent>
          </MobileSidebar>
        )}

        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col min-w-0">
          <SheetHeader className="p-6 pb-3 border-b flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 ml-10 md:ml-0">
                <Bot className="h-5 w-5 text-blue-500" />
                <div>
                  <SheetTitle>EVE's Chatbot</SheetTitle>
                  <p className="text-sm text-gray-600 mt-1">
                    Current session: {currentSessionName}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleNewSessionInDrawer}
                  className="text-blue-500 hover:text-blue-700"
                >
                  New Session
                </Button>
                <Button
                  data-testid="close-fullscreen"
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <Minimize className="h-4 w-4" />
                  Minimize
                </Button>
              </div>
            </div>
            <p className="text-sm text-gray-600 mt-2">
              Ask questions about your products, customers, and orders for {organisation?.name || 'your organization'}.
            </p>
          </SheetHeader>
          
          <div className="flex-1 flex flex-col min-h-0">
            {/* Tool Execution Status */}
            {currentToolCall && (
              <div className="bg-blue-50 border-b border-blue-100 px-6 py-2 flex items-center gap-2 text-sm text-blue-700">
                <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full" />
                <span className="font-medium">{currentToolCall}</span>
              </div>
            )}
            
            <div className="flex-1 min-h-0 p-6 pb-2">
              <MessageList 
                messages={messages} 
                messagesEndRef={messagesEndRef}
                isLoadingHistory={isLoadingHistory}
                currentSessionId={currentSessionId}
              />
            </div>
              
            <div className="flex-shrink-0 p-6 pt-2 border-t bg-white">
              <ChatInput 
                isLoading={isLoading} 
                onSendMessage={onSendMessage}
                settings={settings}
                onSettingsChange={onSettingsChange}
              />
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default FullscreenChatDrawer;
